import { jsxs, jsx } from "react/jsx-runtime";
import { ConciergeRetailerIconGroup } from "../../../components/ConciergeRetailerIconGroup.js";
import { useIsCompactVoiceLayout } from "../../hooks/useIsCompactVoiceLayout.js";
import { AmazonStarRating } from "./AmazonStarRating.js";
const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;
function formatPrice(price) {
  if (typeof price === "number") return `$${price.toFixed(2)}`;
  if (typeof price === "string") return price;
  return "";
}
function ProductCard({ product }) {
  const href = product.dtcProductPageUrl || product.retailerProductPageUrl;
  const showRetailerIcons = typeof product.retailerId === "number" && product.retailerId > 0 && product.retailerId < 1e5;
  const linkProps = href ? { href, target: "_blank", rel: "noreferrer" } : null;
  const priceStr = formatPrice(product.price);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        position: "relative",
        background: "var(--voice-widget-card-bg, #FFFFFF)",
        border: "1px solid var(--voice-widget-border, #E5E7EB)",
        borderRadius: 16,
        padding: "16px 12px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        transition: "box-shadow 160ms ease-out, transform 160ms ease-out",
        animation: "voice-widget-fade-in 0.28s ease-out both",
        height: "100%",
        // Per-card max-width cap (2026-05-19): keeps each product card
        // narrow + scannable even when there's only 1-2 cards on screen.
        // Grid `justifyContent: center` (see dispatcher) centers the row
        // so a single card isn't full-bleed.
        maxWidth: 230,
        width: "100%",
        boxSizing: "border-box",
        minWidth: 0
      },
      children: [
        product.imageUrl && (linkProps ? /* @__PURE__ */ jsx("a", { ...linkProps, style: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx(
          "img",
          {
            src: product.imageUrl,
            alt: product.title,
            style: {
              width: 100,
              height: 100,
              objectFit: "contain",
              borderRadius: 8,
              background: "var(--voice-widget-img-bg, #F9FAFB)"
            }
          }
        ) }) : /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "center" }, children: /* @__PURE__ */ jsx(
          "img",
          {
            src: product.imageUrl,
            alt: product.title,
            style: {
              width: 100,
              height: 100,
              objectFit: "contain",
              borderRadius: 8,
              background: "var(--voice-widget-img-bg, #F9FAFB)"
            }
          }
        ) })),
        product.brand && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              fontSize: 11,
              fontWeight: 600,
              color: "var(--voice-widget-muted, #6B7280)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              lineHeight: 1.2
            },
            children: product.brand
          }
        ),
        linkProps ? /* @__PURE__ */ jsx(
          "a",
          {
            ...linkProps,
            style: {
              fontSize: 13,
              fontWeight: 600,
              color: "var(--voice-widget-fg, #111827)",
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "calc(13px * 1.35 * 2)",
              textDecoration: "none"
            },
            children: product.title
          }
        ) : /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              fontSize: 13,
              fontWeight: 600,
              color: "var(--voice-widget-fg, #111827)",
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "calc(13px * 1.35 * 2)"
            },
            children: product.title
          }
        ),
        product.badge && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              fontSize: 10,
              fontWeight: 600,
              color: "var(--voice-widget-badge-fg, #059669)",
              background: "var(--voice-widget-badge-bg, #ECFDF5)",
              padding: "2px 8px",
              borderRadius: 999,
              alignSelf: "flex-start"
            },
            children: product.badge
          }
        ),
        /* @__PURE__ */ jsx(
          AmazonStarRating,
          {
            amazonRating: product.amazonRating,
            amazonReviewCount: product.amazonReviewCount,
            size: 13,
            countFontSize: 11
          }
        ),
        (showRetailerIcons || product.retailerName) && /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 6, minHeight: 18 }, children: [
          showRetailerIcons && /* @__PURE__ */ jsx(ConciergeRetailerIconGroup, { retailerIds: [product.retailerId], iconSize: 16, maxSize: 3 }),
          !showRetailerIcons && product.retailerName && /* @__PURE__ */ jsx("span", { style: { fontSize: 11, color: "var(--voice-widget-muted, #6B7280)" }, children: product.retailerName })
        ] }),
        /* @__PURE__ */ jsxs(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 6,
              marginTop: "auto"
            },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    fontSize: 17,
                    fontWeight: 700,
                    color: "var(--voice-widget-price, #111827)",
                    lineHeight: 1.1
                  },
                  children: priceStr
                }
              ),
              product.hasRewards && product.totalRewardAmount != null && product.totalRewardAmount > 0 && /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    fontSize: 10,
                    fontWeight: 600,
                    color: "var(--voice-widget-badge-fg, #059669)",
                    background: "var(--voice-widget-badge-bg, #ECFDF5)",
                    padding: "2px 7px",
                    borderRadius: 999,
                    whiteSpace: "nowrap"
                  },
                  children: [
                    "$",
                    product.totalRewardAmount.toFixed(2),
                    " back"
                  ]
                }
              )
            ]
          }
        ),
        product.isAvailable === false && /* @__PURE__ */ jsx("div", { style: { fontSize: 11, fontWeight: 600, color: "var(--voice-widget-warn, #DC2626)" }, children: "Out of stock" }),
        linkProps && /* @__PURE__ */ jsx(
          "a",
          {
            ...linkProps,
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: 32,
              background: "var(--voice-widget-button-bg, #111827)",
              color: "var(--voice-widget-button-fg, #FFFFFF)",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              textDecoration: "none",
              marginTop: 6,
              flexShrink: 0
            },
            children: "View product"
          }
        )
      ]
    }
  );
}
function renderAttributeCell(attr, product) {
  switch (attr.type) {
    case "price": {
      const v = product.price;
      if (v == null || v === "") return "—";
      const num = typeof v === "number" ? v : Number(v.replace(/[^\d.-]/g, ""));
      if (!Number.isFinite(num) || num <= 0) return typeof v === "string" ? v : "—";
      return /* @__PURE__ */ jsxs("span", { style: { fontWeight: 700, color: "var(--voice-widget-price, #111827)" }, children: [
        "$",
        num.toFixed(2)
      ] });
    }
    case "rating": {
      if (typeof product.amazonRating !== "number" || product.amazonRating <= 0) return "—";
      return /* @__PURE__ */ jsx(
        AmazonStarRating,
        {
          amazonRating: product.amazonRating,
          amazonReviewCount: product.amazonReviewCount
        }
      );
    }
    case "availability":
      if (product.isAvailable == null) return "—";
      return product.isAvailable ? /* @__PURE__ */ jsx("span", { style: { color: "var(--voice-widget-badge-fg, #059669)", fontWeight: 600, fontSize: 12 }, children: "In stock" }) : /* @__PURE__ */ jsx("span", { style: { color: "var(--voice-widget-warn, #DC2626)", fontWeight: 600, fontSize: 12 }, children: "Out of stock" });
    case "reward":
      return product.hasRewards && product.totalRewardAmount != null && product.totalRewardAmount > 0 ? /* @__PURE__ */ jsxs(
        "span",
        {
          style: {
            fontSize: 11,
            fontWeight: 600,
            color: "var(--voice-widget-badge-fg, #059669)",
            background: "var(--voice-widget-badge-bg, #ECFDF5)",
            padding: "2px 8px",
            borderRadius: 999
          },
          children: [
            "$",
            product.totalRewardAmount.toFixed(2),
            " back"
          ]
        }
      ) : /* @__PURE__ */ jsx("span", { style: { color: "var(--voice-widget-muted, #6B7280)" }, children: "None" });
    case "bullets": {
      const bullets = product[attr.key];
      if (!Array.isArray(bullets) || bullets.length === 0) return "—";
      return /* @__PURE__ */ jsx("ul", { style: { margin: 0, paddingLeft: 16, fontSize: 11, lineHeight: 1.5, color: "var(--voice-widget-fg, #111827)" }, children: bullets.slice(0, 3).map((b, i) => /* @__PURE__ */ jsx("li", { children: b }, i)) });
    }
    default: {
      const raw = product[attr.key];
      if (raw == null || raw === "" || raw === "—") return "—";
      return String(raw);
    }
  }
}
const ComparisonTable = ({ products, attributes, isCompact }) => {
  const labelColWidth = isCompact ? 100 : 110;
  const productColMinWidth = isCompact ? 150 : 0;
  const tableMinWidth = isCompact ? labelColWidth + productColMinWidth * products.length : void 0;
  return /* @__PURE__ */ jsx(
    "div",
    {
      style: {
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 8px 12px",
        overflowX: isCompact ? "auto" : "visible",
        WebkitOverflowScrolling: "touch"
      },
      children: /* @__PURE__ */ jsxs(
        "table",
        {
          style: {
            width: "100%",
            minWidth: tableMinWidth,
            borderCollapse: "separate",
            borderSpacing: 0,
            background: "var(--voice-widget-card-bg, #FFFFFF)",
            borderRadius: 14,
            overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            tableLayout: "fixed",
            fontSize: 12,
            color: "var(--voice-widget-fg, #111827)"
          },
          children: [
            /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
              /* @__PURE__ */ jsx(
                "th",
                {
                  style: {
                    width: labelColWidth,
                    background: "var(--voice-widget-table-head-bg, #F9FAFB)",
                    borderBottom: "1px solid var(--voice-widget-border, #E5E7EB)"
                  }
                }
              ),
              products.map((p) => {
                const href = p.dtcProductPageUrl || p.retailerProductPageUrl;
                const linkProps = href ? { href, target: "_blank", rel: "noreferrer" } : null;
                return /* @__PURE__ */ jsx(
                  "th",
                  {
                    style: {
                      padding: "14px 12px 10px",
                      textAlign: "center",
                      verticalAlign: "top",
                      fontWeight: 600,
                      borderBottom: "1px solid var(--voice-widget-border, #E5E7EB)",
                      background: "var(--voice-widget-table-head-bg, #F9FAFB)"
                    },
                    children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }, children: [
                      p.imageUrl && (linkProps ? /* @__PURE__ */ jsx("a", { ...linkProps, style: { lineHeight: 0 }, children: /* @__PURE__ */ jsx(
                        "img",
                        {
                          src: p.imageUrl,
                          alt: p.title,
                          style: {
                            width: 64,
                            height: 64,
                            objectFit: "contain",
                            borderRadius: 6,
                            background: "var(--voice-widget-img-bg, #F9FAFB)"
                          }
                        }
                      ) }) : /* @__PURE__ */ jsx(
                        "img",
                        {
                          src: p.imageUrl,
                          alt: p.title,
                          style: {
                            width: 64,
                            height: 64,
                            objectFit: "contain",
                            borderRadius: 6,
                            background: "var(--voice-widget-img-bg, #F9FAFB)"
                          }
                        }
                      )),
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          style: {
                            fontSize: 12,
                            fontWeight: 600,
                            color: "var(--voice-widget-fg, #111827)",
                            lineHeight: 1.3,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden"
                          },
                          children: p.title
                        }
                      ),
                      linkProps && /* @__PURE__ */ jsx(
                        "a",
                        {
                          ...linkProps,
                          style: {
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "var(--voice-widget-button-bg, #111827)",
                            color: "var(--voice-widget-button-fg, #FFFFFF)",
                            borderRadius: 999,
                            padding: "6px 14px",
                            fontSize: 11,
                            fontWeight: 600,
                            textDecoration: "none"
                          },
                          children: "View product"
                        }
                      )
                    ] })
                  },
                  p.stacklineSku
                );
              })
            ] }) }),
            /* @__PURE__ */ jsx("tbody", { children: attributes.map((attr, rowIdx) => /* @__PURE__ */ jsxs(
              "tr",
              {
                style: {
                  background: rowIdx % 2 === 0 ? "var(--voice-widget-card-bg, #FFFFFF)" : "var(--voice-widget-table-row-alt-bg, #F9FAFB)"
                },
                children: [
                  /* @__PURE__ */ jsx(
                    "td",
                    {
                      style: {
                        padding: "10px 12px",
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: 0.3,
                        color: "var(--voice-widget-muted, #6B7280)",
                        borderBottom: "1px solid var(--voice-widget-border, #E5E7EB)",
                        verticalAlign: "top"
                      },
                      children: attr.label
                    }
                  ),
                  products.map((p) => /* @__PURE__ */ jsx(
                    "td",
                    {
                      style: {
                        padding: "10px 12px",
                        textAlign: "center",
                        verticalAlign: "top",
                        borderBottom: "1px solid var(--voice-widget-border, #E5E7EB)",
                        color: "var(--voice-widget-fg, #111827)",
                        fontSize: 12,
                        lineHeight: 1.4
                      },
                      children: renderAttributeCell(attr, p)
                    },
                    p.stacklineSku
                  ))
                ]
              },
              attr.key
            )) })
          ]
        }
      )
    }
  );
};
const ComparisonWidget = ({ data }) => {
  const products = data.products.slice(0, 3);
  const hasAttributes = Array.isArray(data.attributes) && data.attributes.length > 0;
  const isCompact = useIsCompactVoiceLayout();
  if (hasAttributes) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        style: {
          width: "100%",
          maxWidth: 720,
          padding: "12px 8px 8px",
          boxSizing: "border-box",
          margin: "0 auto"
        },
        children: [
          /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
          /* @__PURE__ */ jsx(
            ComparisonTable,
            {
              products,
              attributes: data.attributes,
              isCompact
            }
          )
        ]
      }
    );
  }
  const gridTemplateColumns = isCompact ? "minmax(0, 230px)" : `repeat(${products.length}, minmax(0, 230px))`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        width: "100%",
        maxWidth: 720,
        padding: "16px 12px 8px",
        boxSizing: "border-box",
        margin: "0 auto"
      },
      children: [
        /* @__PURE__ */ jsx("style", { children: WIDGET_KEYFRAMES }),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              display: "grid",
              gridTemplateColumns,
              justifyContent: "center",
              gap: 10,
              alignItems: "stretch"
            },
            children: products.map((p) => /* @__PURE__ */ jsx(ProductCard, { product: p }, p.stacklineSku))
          }
        )
      ]
    }
  );
};
export {
  ComparisonWidget
};
//# sourceMappingURL=ComparisonWidget.js.map
